import API from 'app/gamma/src/api';
import { MoonshotBox } from './MoonshotBox';
import React, { useReducer, useState, useEffect } from 'react';
import { MemberResponse } from 'app/gamma/src/types/responses';
import {
  useMoonshotInviteTeamMembersGetOrgQuery,
  useMoonshotInviteTeamMembersMutation,
} from '@trello/graphql/generated';
import styles from './MoonshotInviteTeamMembers.less';
import { createTeamInvite } from 'app/gamma/src/util/model-helpers/team';
import classNames from 'classnames';
import { logoDomain } from '@trello/config';
import { Noun, Verb, trackUe } from '@trello/analytics';
import { IconName, Icon } from 'app/common/components/controls';
import { Spinner } from 'app/src/components/Spinner';
import Alerts from 'app/scripts/views/lib/alerts.coffee';
import { MoonshotSubmitFunction } from './types';
import { MoonshotSteps } from 'app/common/data/campaigns';
import { useFeatureFlag } from '@trello/feature-flag-client';
import { Analytics } from '@trello/atlassian-analytics';

const MemberMultiSelectAutocompleteComponent = require('app/scripts/views/member/member-multi-select-autocomplete-component');

const ERROR_MESSAGE =
  'There was an unexpected error. Please refresh and try again';

const onMemberSearchFetch = (
  query: string,
  idOrganization = '',
): Promise<MemberResponse[]> => {
  if (query.length > 2) {
    return API.client.searchMembers({
      query: query,
      idOrganization,
    });
  } else {
    return Promise.resolve([]);
  }
};

const memberReducer = (
  state: MemberResponse[],
  action:
    | {
        type: 'add';
        member: MemberResponse;
      }
    | {
        type: 'remove';
        memberIndex: number;
      },
) => {
  switch (action.type) {
    case 'add':
      return [...state, action.member];
    case 'remove':
      return state.filter((_, i) => i !== action.memberIndex);
    default:
      return state;
  }
};

interface MoonshotInviteTeamMembersProps {
  orgId: string;
  onSubmit: MoonshotSubmitFunction;
  // This allows the ability to override the search query
  // for things like tests and storybook
  onMemberSearch?: typeof onMemberSearchFetch;
}

export const MoonshotInviteTeamMembers: React.FC<MoonshotInviteTeamMembersProps> = ({
  orgId,
  onMemberSearch = onMemberSearchFetch,
  onSubmit,
}) => {
  const [addMembersToOrg] = useMoonshotInviteTeamMembersMutation();
  const { data, loading } = useMoonshotInviteTeamMembersGetOrgQuery({
    variables: { orgId },
  });

  const [selectedMembers, updateMembers] = useReducer(memberReducer, []);
  const [invitationMessage, setInvitationMessage] = useState('');
  const [invitationUrl, setInvitationUrl] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const messageEnabledFlag = useFeatureFlag(
    'enable-invite-email-message',
    false,
  );

  const sendGASEvent = useFeatureFlag('dataeng.gasv3-event-tracking', false);

  const org = data && data.organization;

  const inviteUrlInputRef = React.createRef<HTMLInputElement>();

  useEffect(() => {
    if (org) {
      setInvitationMessage(
        `Hi, I’d like to invite you to join ${org.displayName} on Trello. We use Trello to organize tasks, projects, due dates and much more.`,
      );
    }
  }, [org]);

  const onClickInvitationUrl: React.MouseEventHandler = async (e) => {
    e.preventDefault();

    if (!org) {
      return;
    }

    setInvitationUrl('Creating...');

    try {
      setInvitationUrl(
        await createTeamInvite(orgId, org.name || '', org.url || ''),
      );
    } catch {
      setInvitationUrl('Sorry something went wrong.');
    }
  };
  const onClickCopyUrl = () => {
    if (inviteUrlInputRef && inviteUrlInputRef.current) {
      inviteUrlInputRef.current.focus();
      document.execCommand('copy');
    }
  };
  const onSubmitWithTracking = async () => {
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    trackUe({
      category: Noun.MOONSHOT,
      verb: Verb.SENDS,
      directObj: Noun.TEAM_INVITATION,
      context: { numInvited: selectedMembers.length },
    });

    if (sendGASEvent) {
      Analytics.sendTrackEvent({
        action: 'sent',
        actionSubject: 'invitation',
        actionSubjectId: 'teamInvitation',
        source: 'moonshotInviteMembersScreen',
        attributes: {
          numInvited: selectedMembers.length,
        },
      });
    }

    if (selectedMembers.length) {
      let errorMsg = '';

      try {
        const { data: addMembersData } = await addMembersToOrg({
          variables: {
            orgId,
            users: selectedMembers,
            invitationMessage,
          },
        });

        if (
          addMembersData &&
          addMembersData.addMembersToOrg &&
          addMembersData.addMembersToOrg.error
        ) {
          errorMsg = ERROR_MESSAGE;
        }
      } catch (err) {
        errorMsg = ERROR_MESSAGE;
      }

      if (errorMsg) {
        Alerts.showLiteralText(errorMsg, 'error', 'alert', 5000);
      }
    }

    onSubmit(MoonshotSteps.PAYMENT);
    setIsSubmitting(false);
  };

  return loading ? (
    <Spinner centered />
  ) : !org ? (
    <>Team not found. Please refresh and try again.</>
  ) : (
    <MoonshotBox className={styles.inviteTeamMembers}>
      <div className={styles.moonshotInviteHeading}>
        <div className={styles.moonshotInviteOrgImage}>
          {org.logoHash ? (
            <img
              src={`${logoDomain}/${org.logoHash}/170.png`}
              alt="Team logo"
            />
          ) : (
            <Icon
              name={IconName.Organization}
              className={styles.moonshotInviteOrgIcon}
            />
          )}
        </div>
        <h1 className={styles.moonshotInviteOrgName}>{org.displayName}</h1>
      </div>
      <h2>
        Loop in your Team Members<em>You can do this later, too!</em>
      </h2>
      <MemberMultiSelectAutocompleteComponent
        submitAlwaysEnabled
        fullWidthButton
        shouldFocusOnSelectMemberOption
        displayDetails
        displayUsername
        placeholder="Type another email address..."
        emptySearchText="Looks like that person isn't a Trello member yet. Add their email address to invite them."
        submitText={isSubmitting ? <Spinner centered /> : 'Next, Select a Plan'}
        onSubmit={onSubmitWithTracking}
        onAutocompleteSearch={(query: string) => onMemberSearch(query, orgId)}
        onSelectMemberOption={(members: MemberResponse | MemberResponse[]) => {
          if (members instanceof Array) {
            members.map((member) => updateMembers({ type: 'add', member }));
          } else {
            updateMembers({ type: 'add', member: members });
          }
        }}
        onRemoveMemberOption={(removedIndex: number) => {
          updateMembers({ type: 'remove', memberIndex: removedIndex });
        }}
        selectedOptions={selectedMembers}
      >
        <p className={styles.small}>
          <strong>Tip!</strong> You can paste as many emails here as needed.
        </p>
        {messageEnabledFlag && (
          <>
            <h3>Include a personal message</h3>
            <textarea
              value={invitationMessage}
              onChange={(event) => {
                setInvitationMessage(event.target.value);
              }}
              maxLength={500}
            />
          </>
        )}
        <p className={classNames({ [styles.smallMargin]: invitationUrl })}>
          Allow your team to join on their own:{' '}
          {!invitationUrl && (
            <button
              className={styles.createInviteButton}
              onClick={onClickInvitationUrl}
            >
              Create an Invite Link
            </button>
          )}
        </p>
        {invitationUrl && (
          <div className={styles.inviteUrlWrapper}>
            <input
              readOnly
              value={invitationUrl}
              className={styles.inviteValue}
              ref={inviteUrlInputRef}
              onFocus={(e) => {
                e.target.select();
              }}
            />
            <button onClick={onClickCopyUrl}>Copy</button>
          </div>
        )}
        <div className={styles.buttonSpacer} />
      </MemberMultiSelectAutocompleteComponent>
    </MoonshotBox>
  );
};
